<template>
  <main class="profile-page-wrapper">
    <h1>Profile and Bank Account</h1>
    <p>
      Please fill in all your profile and bank account details in order to
      receive your local currency withdrawals.
    </p>
    <form @submit.prevent>
      <label class="withdrawal-account">
        Merchant
        <select
          class="account-selection style-select"
          data-class="{'select-icon-validate-danger' : validateMerchantSelectionError}"
          v-model="selectedMerchantKey"
          @change="onChangeMerchant(selectedMerchantKey)">
          <optgroup label="Merchant List">
            <option
              v-for="(item, index) in merchantsList"
              :value="item.merchant_key"
              :key="index"
              v-if="checkMerchantsToDisplay.some(
                (merchant) => merchant.id === item.id
              )
                ">
              {{ item.merchant_name }}
            </option>
          </optgroup>
        </select>
      </label>
      <!-- PROFILE -->
      <fieldset v-if="displayProfileAndBankAccount" class="profile-container" :disabled="!hasLocalCurrencyWallet">
        <legend>Profile</legend>
        <p v-if="!hasLocalCurrencyWallet" class="banner info">
          This merchant's activation is currently pending. To kickstart the process, you can get started by reaching out to <a href="mailto:account.verification@triple-a.io">our dedicated KYB team</a>.
          If you've already submitted your application, all that's left to do is patiently await approval.
          Should you have any inquiries or need assistance at any point, <a href="mailto:support@triple-a.io">our support team</a> is just a message away.
        </p>
        <label v-if="hasLocalCurrencyWallet">
          Preferred Local Currency
          <input disabled v-model="preferredLocalCurrency" />
        </label>
        <label>
          Name
          <input disabled v-model.trim="merchantName" />
        </label>
        <label>
          Identification Type
          <span class="req">*</span>
          <select
            id="businessType"
            class="account-selection w-full style-select"
            data-class="{'select-icon-validate-danger' : validateMerchantSelectionError}"
            v-model="idType"
            :class="{
              'input-error-style': validateMerchantIdentificationTypeError,
            }"
            :disabled="!hasLocalCurrencyWallet">
            <option
              v-for="(item, index) in businessType"
              :value="item.value"
              :key="index">
              {{ item.name }}
            </option>
            <span
              v-if="validateMerchantIdentificationTypeError"
              style="font-size: 10px; color: red">Please select the identification type.</span>
          </select>
        </label>
        <label>
          Identification Number
          <span class="req">*</span>
          <input
            type="text"
            name="uniqueEntityIdentifier"
            ref="uniqueEntityIdentifier"
            v-model.trim="uniqueEntityIdentifier"
            class="w-full mt-0 no-icon-border mb-0 input-text-style"
            maxlength="20"
            :class="{
              'input-error-style': validateMerchantIdentificationNumberError,
            }"
            :disabled="!idType" />
          <span
            v-if="validateMerchantIdentificationNumberError"
            style="font-size: 10px; color: red">Please insert a valid identification number.</span>
        </label>
        <label>
          Country
          <input disabled v-model.trim="companyCountry.name" v-if="companyCountry" />
          <p v-else>
            Please contact
            <a href="mailto:account.verification@triple-a.io">our team</a>
            to complete the account verification process.
          </p>
        </label>
        <label>
          Registered Address
          <span class="req">*</span>
          <textarea
            name="companyAddress"
            ref="companyAddress"
            v-model.trim="companyAddress"
            class="w-full mt-0 no-icon-border mb-0 input-textarea-style"
            :class="{
              'input-error-style': validateMerchantRegisteredAddressError,
            }" />
          <span
            v-if="validateMerchantRegisteredAddressError"
            style="font-size: 10px; color: red">Please insert a valid registered address.</span>
        </label>

        <label>
          Contact Name
          <span class="req">*</span>
          <input
            type="text"
            name="contactName"
            ref="contactName"
            v-model.trim="contactName"
            class="w-full mt-0 no-icon-border mb-0 input-text-style"
            :class="{
              'input-error-style': validateMerchantContactNameError,
            }" />
          <span
            v-if="validateMerchantContactNameError"
            style="font-size: 10px; color: red">Please insert a contact name.</span>
        </label>

        <div for="tel-input">
          Contact Phone
          <span class="req">*</span>
          <vue-tel-input
            v-model="contactPhone"
            class="tel-selection"
            v-bind="phoneValidation"
            :disabled="!hasLocalCurrencyWallet"
            :class="{
              'input-error-style': validateMerchantContactPhoneError,
            }"
            defaultCountry="SG"></vue-tel-input>
          <span
            v-if="validateMerchantContactPhoneError"
            style="font-size: 10px; color: red">Please insert a valid contact phone (eg.+countrycode phone
            number).</span>
        </div>

        <label>
          Contact Email
          <span class="req">*</span>
          <input
            type="text"
            name="contactEmail"
            ref="contactEmail"
            v-model.trim="contactEmail"
            class="w-full mt-0 no-icon-border mb-0 input-text-style"
            :class="{
              'input-error-style': validateMerchantContactEmailError,
            }" />
          <span
            v-if="validateMerchantContactEmailError"
            style="font-size: 10px; color: red">Please insert a valid contact email.</span>
        </label>

        <label>
          Notification Email
          <span class="req">*</span>
          <span class="tooltip">
            <info-icon size="1x"></info-icon>
            <span>
              This email will be used by Triple-A to send notification regarding
              your transactions or payouts with Triple-A . If you have multiple
              merchants, please ensure the email is unique to that merchant.
            </span>
          </span>
          <input
            type="text"
            name="notificationEmail"
            ref="notificationEmail"
            v-model.trim="notificationEmail"
            class="w-full mt-0 no-icon-border mb-0 input-text-style"
            :class="{
              'input-error-style': validateMerchantNotificationEmailError,
            }" />
          <span
            v-if="validateMerchantNotificationEmailError"
            style="font-size: 10px; color: red">Notification email should be valid and unique to this
            merchant.</span>
        </label>

        <label>
          Website
          <span class="req">*</span>
          <input
            type="text"
            name="companyWebsite"
            ref="companyWebsite"
            v-model="companyWebsite"
            class="w-full mt-0 no-icon-border mb-0 input-text-style"
            :class="{
              'input-error-style': validateMerchantWebsiteError,
            }" />
          <span
            v-if="validateMerchantWebsiteError"
            style="font-size: 10px; color: red">Please insert a valid website. Insert N/A if none.</span>
        </label>

        <label>
          Technical Contact Email
          <span class="tooltip">
            <info-icon size="1x"></info-icon>
            <span>
              This will be used for technical or service notifications related
              to Triple-A’s APIs and products.
            </span>
          </span>
          <input
            type="text"
            name="technicalEmailAddress"
            ref="technicalEmailAddress"
            v-model.trim="technicalEmail"
            class="w-full mt-0 no-icon-border mb-0 input-text-style"
            :class="{
              'input-error-style': validateMerchantTechnicalError,
            }" />
          <span
            v-if="validateMerchantTechnicalError"
            style="font-size: 10px; color: red">Please insert a valid technical email address.</span>
        </label>

        <label class="full-row">
          Finance Contact Email
          <span class="tooltip">
            <info-icon size="1x"></info-icon>
            <span>
              This email will be used by Triple-A to send finance-related
              notifications. If left blank, it will default to the email under
              "Notification Email".
            </span>
          </span>
          <input
            type="text"
            name="financeEmailAddress"
            ref="financeEmailAddress"
            v-model.trim="financeEmails"
            class="w-full mt-0 no-icon-border mb-0 input-text-style"
            :class="{
              'input-error-style': validateMerchantFinanceError,
            }" />
          <span style="font-size: 10px">For multiple emails, separate the emails with a comma.</span>
          <br />
          <span
            v-if="validateMerchantFinanceError"
            style="font-size: 10px; color: red">Please insert a valid finance email address.</span>
        </label>
        <div class="errors-msg">
          <div v-if="errorMerchantProfileMsg">Please enter a valid value or the required field before
            saving.</div>
          <div v-else-if="apiErrorSavingMerchantProfile">
            Error in saving merchant. Please try again in a few minutes.
          </div>
          <div v-else-if="unauthorisedAccessForMerchantProfileError">
            Incorrect authorisation for updating merchant profile. Please try again.
          </div>
        </div>
        <div class="success-msg" v-if="saveMerchantProfileSuccess">
          <check-icon class="mr-1"></check-icon>
          Saved!
        </div>
        <button
          @click="
            enabled2FAForProfile
              ? (show2faPrompt = true)
              : saveMerchantProfile()
            "
          :disabled="!hasLocalCurrencyWallet">
          Save Profile
        </button>
      </fieldset>
    </form>

    <!-- BANK ACCOUNT DETAILS -->
    <section
      v-if="displayProfileAndBankAccount"
      class="bank-accounts-container">
      <bank-account-form
        :bankAccount="bankAccount"
        :isLocalCurrencyWallet="hasLocalCurrencyWallet"
        hasBanner
        :legalEntityName="legalEntityName"></bank-account-form>
      <bank-account-form
        v-if="hasCorrespondentBank"
        :bankAccount="correspondentBankAccount"
        :isLocalCurrencyWallet="hasLocalCurrencyWallet"
        sectionName="Correspondent Bank Account"
        :legalEntityName="legalEntityName"></bank-account-form>
      <div class="save-bank-success" v-if="saveBankAccountSuccess">
        <check-icon class="mr-1"></check-icon>
        Saved!
      </div>
      <div class="save-error" v-if="errorBankAccountMsg">
        There is an error in creating or updating bank account. Please try again.
      </div>
      <div class="save-error" v-if="unauthorisedAccessForBankAccountError">
        Incorrect authorisation for updating bank account. Please try again.
      </div>
      <div class="action-group" v-if="hasLocalCurrencyWallet">
        <p class="banner icon warning" v-if="needsCompanyAddress">
          Please fill in your company address before trying to save your bank account details.
        </p>
        <button
          @click="
            enabled2FAForBankAccount
              ? (show2faOtpPrompt = true)
              : saveMerchantBankAccount()
            "
          :disabled="needsCompanyAddress">
          Save bank accounts
        </button>
        <input
          type="checkbox"
          v-model="hasCorrespondentBank"
          class="corr-bank-switch"
          id="corr-bank-toggle" />
        <label for="corr-bank-toggle">
          {{
          hasCorrespondentBank
          ? 'Remove Correspondant Bank'
          : 'Add Correspondent Bank'
          }}
        </label>
      </div>
    </section>
    <p v-else>
      You do not have permission to view or access this page for this merchant.
    </p>
    <otp-prompt
      :login="loginEmail"
      :active.sync="show2faOtpPrompt"
      otpRequired
      @verify="saveMerchantBankAccount($event)" />

    <email-password-prompt
      :login="loginEmail"
      :active.sync="show2faPrompt"
      @verify="saveMerchantProfile($event)" />
  </main>
</template>

<script>
import Vue from 'vue';
import vSelect from 'vue-select';
import VueCountryCode from 'vue-country-code';
import { VueTelInput } from 'vue-tel-input';
import { mapGetters, mapActions } from 'vuex';
import { InfoIcon, CheckIcon } from 'vue-feather-icons';
import EmailPasswordPrompt from '@/components/EmailPasswordPrompt';
import OtpPrompt from '@/components/OtpPrompt';
import { COUNTRIES } from '../../constants';
import BankAccountForm from '../../components/manage-profile/BankAccountForm.vue';

Vue.use(VueTelInput);
Vue.use(VueCountryCode);

export default {
  components: {
    vSelect,
    VueCountryCode,
    VueTelInput,
    InfoIcon,
    CheckIcon,
    EmailPasswordPrompt,
    OtpPrompt,
    BankAccountForm,
  },
  data() {
    return {
      bankAccount: {
        accountName: {
          value: '',
          error: false,
          isRequired: true,
        },
        accountNumber: {
          value: '',
          error: false,
          isRequired: true,
        },
        bankName: {
          value: '',
          error: false,
          isRequired: true,
        },
        bankCountry: {
          value: '',
          error: false,
          isRequired: true,
        },
        swiftCode: {
          value: '',
          error: false,
          isRequired: true,
        },
        routingNumber: {
          value: '',
          error: false,
          isRequired: true,
        },
        bankAddress: {
          value: '',
          error: false,
          isRequired: true,
        },
        bankClearingCode: {
          value: '',
          error: false,
          isRequired: true,
        },
      },
      needsCompanyAddress: false,

      hasCorrespondentBank: false,
      correspondentBankAccount: {
        accountName: {
          value: '',
          error: false,
          isRequired: true,
        },
        accountNumber: {
          value: '',
          error: false,
          isRequired: true,
        },
        bankName: {
          value: '',
          error: false,
          isRequired: true,
        },
        bankCountry: {
          value: '',
          error: false,
          isRequired: true,
        },
        swiftCode: {
          value: '',
          error: false,
          isRequired: true,
        },
        routingNumber: {
          value: '',
          error: false,
          isRequired: true,
        },
        bankAddress: {
          value: '',
          error: false,
          isRequired: true,
        },
        bankClearingCode: {
          value: '',
          error: false,
          isRequired: true,
        },
      },

      countries: COUNTRIES,
      businessType: [
        {
          name: 'Business Registration',
          value: 'biz-reg-num',
        },
        {
          name: 'National ID',
          value: 'national-id-num',
        },
        {
          name: 'Passport',
          value: 'passport-num',
        },
      ],

      selectedMerchantKey: null,
      selectedMerchantProfile: null,
      saveMerchantProfileSuccess: false,
      errorMerchantProfileMsg: false,
      apiErrorSavingMerchantProfile: false,

      //profile (KYC)
      merchantName: '',
      companyAddress: '',
      companyCountry: '',
      companyWebsite: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      idType: '',
      uniqueEntityIdentifier: '',
      preferredLocalCurrency: '', // only show for wallet using local currency settlement.
      notificationEmail: '',
      technicalEmail: '',
      financeEmails: '',
      legalEntityName: '',

      selectedBankAccount: null,
      hasLocalCurrencyWallet: false,
      saveBankAccountSuccess: false,
      errorBankAccountMsg: false,
      apiSavingMerchantBankDetailsError: false,

      debugConsole: false,
      show2faPrompt: false,
      loginEmail: this.$store.state.activeUser.email,
      show2faOtpPrompt: false,

      //displays content depends on the user's permission
      displayProfileAndBankAccount: false,

      //check if 2fa is enabled
      enabled2FAForProfile: false,
      enabled2FAForBankAccount: false,

      // Validation for profile
      phoneValidation: {
        inputOptions: {
          showDialCode: true,
          name: 'tel-input',
        },
        placeholder: 'Enter a phone number',
        disabledFetchingCountry: true,
        enabledCountryCode: false,
        validCharactersOnly: true,
      },
      validatePreferredLocalCurrencyError: false,
      validateMerchantIdentificationTypeError: false,
      validateMerchantIdentificationNumberError: false,
      validateMerchantRegisteredAddressError: false,
      validateMerchantCountryError: false,
      validateMerchantContactNameError: false,
      validateMerchantContactPhoneError: false,
      validateMerchantContactEmailError: false,
      validateMerchantNotificationEmailError: false,
      validateMerchantWebsiteError: false,
      validateMerchantTechnicalError: false,
      validateMerchantFinanceError: false,
      
      unauthorisedAccessForMerchantProfileError: false,
      unauthorisedAccessForBankAccountError: false,

      recaptchaToken: '',
    };
  },
  computed: {
    ...mapGetters([
      'activeAccountGetter',
      'localCurrencyGetter',
      'merchantsGetter',
      'activeMerchantGetter',
      'storedActiveMerchantGetter',
      'merchantBankAccountGetter',
      'merchantProfileDetailsGetter',
      'currentActiveEntityAdminGetter',
    ]),

    checkMerchantsToDisplay() {
      //returns an array of merchants this user has permission to
      return this.hasGroupPermissionToMerchants(
        this.currentActiveEntityAdminGetter
      );
    },

    merchantsList() {
      return this.merchantsGetter;
    },

    //select the first merchants' bank account available
    // TODO take into account for merchants' with multiple bank accounts
    merchantFirstBankAccount() {
      return this.merchantBankAccountGetter[0];
    },

    getBankAccountBankCountry() {
      return this.bankAccount.bankCountry.value;
    },
    getCorrespondentBankAccountBankCountry() {
      return this.correspondentBankAccount.bankCountry.value;
    },
  },
  mounted() {
    this.account = this.activeAccountGetter;
    if (!this.account || !this.account.apiId) {
      console.error('No active wallet found.');
      // this.$router.push('add-account');
      this.hasLocalCurrencyWallet = false;
      return;
    } else {
      //check if default selected merchant is using local currency wallet
      this.hasLocalCurrencyWallet = this.activeMerchantGetter.accounts.find(
        (item) => {
          return (
            item.crypto_currency === 'BTC' &&
            item.settlement_type === 'localcurrency'
          );
        }
      )
        ? true
        : false;

      //default merchant when the page loads depends on the active Account
      this.selectedMerchantKey = this.storedActiveMerchantGetter
        ? this.storedActiveMerchantGetter.merchant_key
        : this.activeMerchantGetter.merchant_key;

      if (this.selectedMerchantKey) {
        this.fetchMerchantDetailsHandler(this.selectedMerchantKey);
        this.checkPermissionHandler(
          this.activeMerchantGetter,
          this.selectedMerchantKey
        );
        this.getRecaptcha().then((token) => {
          const payload = {
            mkey: this.selectedMerchantKey,
            recaptchaToken: token || this.recaptchaToken
          };
          this.getLegalEntityNameMerchant(payload)
            .then(
              res => {
                this.legalEntityName = res.data.legal_entity.legal_entity_name;
              })
            .catch(
              err => {
                console.log(err);
              }
            );
        });
      }
    }
  },
  watch: {
    selectedMerchantKey: function (newVal, oldVal) {
      if (this.debugConsole) console.log('Merchant Key old Val', oldVal);
      if (this.debugConsole) console.log('Merchant key new val', newVal);
      this.getRecaptcha().then((token) => {
        const payload = {
          mkey: this.selectedMerchantKey,
          recaptchaToken: token || this.recaptchaToken
        };
        this.getLegalEntityNameMerchant(payload)
          .then(
            res => {
              this.legalEntityName = res.data.legal_entity.legal_entity_name;
            })
          .catch(
            err => {
              console.log(err);
            }
          );
      });
    },
    storedActiveMerchantGetter(newVal) {
      this.selectedMerchantKey = newVal.merchant_key;
      this.onChangeMerchant(newVal.merchant_key);
    },

    getBankAccountBankCountry(newVal) {
      this.bankAccount.bankClearingCode.isRequired = this.isClearingCodeRequired(
        newVal
      );
      this.bankAccount.routingNumber.isRequired = newVal === 'US';
      this.bankAccount.swiftCode.isRequired = newVal !== 'US';
    },

    getCorrespondentBankAccountBankCountry(newVal) {
      this.correspondentBankAccount.bankClearingCode.isRequired = this.isClearingCodeRequired(
        newVal
      );
      this.correspondentBankAccount.routingNumber.isRequired = newVal === 'US';
      this.correspondentBankAccount.swiftCode.isRequired = newVal !== 'US';
    },
  },
  methods: {
    ...mapActions([
      'fetchMerchantBankAccounts',
      'fetchMerchantProfile',
      'saveMerchantProfileDetails',
      'updateMerchantBankAccountDetails',
      'addNewBankAccountDetails',
      'getLegalEntityNameMerchant'
    ]),

    isClearingCodeRequired(countryCode) {
      return countryCode === 'AU';
    },

    toggleCorrespondentBank() {
      this.hasCorrespondentBank = !this.hasCorrespondentBank;
    },

    onChangeMerchant(merchantKey) {
      const currentActiveMerchant = this.merchantsGetter.find(
        (item) => item.merchant_key === merchantKey
      );

      //check if default selected merchant is using local currency wallet
      this.hasLocalCurrencyWallet = currentActiveMerchant.accounts.find((el) => {
        return (
          el.crypto_currency === 'BTC' && el.settlement_type === 'localcurrency'
        );
      })
        ? true
        : false;

      if (merchantKey) {
        this.fetchMerchantDetailsHandler(merchantKey);
        this.checkPermissionHandler(currentActiveMerchant, merchantKey);
        // remove error message when change to other merchant
        this.errorMerchantProfileMsg = false; 
        this.errorBankAccountMsg = false;
      }
    },

    saveMerchantProfile(eventPayload) {
      //check for validation
      const isProfileInputValid = this.isProfileFormValid();
      if (!isProfileInputValid) {
        return;
      }
      this.needsCompanyAddress = !this.companyAddress;

      if (this.selectedMerchantKey) {
        let payload = {};
        payload.merchant_key = this.selectedMerchantKey;
        payload.data = {};

        if (this.preferredLocalCurrency) {
          payload.data.local_currency = this.preferredLocalCurrency;
        }
        payload.data.merchant_name = this.merchantName || '';
        payload.data.id_type = this.idType || '';
        payload.data.id_num = this.uniqueEntityIdentifier || '';
        payload.data.address = this.companyAddress || '';
        payload.data.country = this.companyCountry.code || '';
        payload.data.website = this.companyWebsite || '';
        payload.data.contact_name = this.contactName || '';
        payload.data.contact_email = this.contactEmail || '';
        payload.data.contact_phone = this.contactPhone || '';
        payload.data.merchant_email = this.notificationEmail || '';
        payload.data.technical_email = this.technicalEmail || '';
        payload.data.finance_emails = this.financeEmails || '';

        if(eventPayload && Object.keys(eventPayload).length > 0) {
          payload.data.username = this.currentActiveEntityAdminGetter.email;
          payload.data[`${Object.keys(eventPayload)[0]}`] = this.encryptPassword(Object.values(eventPayload)[0], this.currentActiveEntityAdminGetter.email);
        }

        if (this.debugConsole) {
          console.log('Save Merchant Profile payload', payload);
        }

        if (payload) {
          this.saveMerchantProfileDetails(payload)
            .then(
              (result) => {
                if (result.status === 200) {
                  this.errorMerchantProfileMsg = false;
                  this.apiErrorSavingMerchantProfile = false;
                  this.saveMerchantProfileSuccess = true;
                  this.validateMerchantNotificationEmailError = false;
                  this.validateMerchantContactPhoneError = false;
                  this.unauthorisedAccessForMerchantProfileError = false;
                  this.selectedMerchantProfile = result.data;                  
                  setTimeout(() => {
                    this.saveMerchantProfileSuccess = false;
                  }, 3000);
                }
              },
            )
            .catch((error) => {
                 if (error.response.status === 422) {
                  if (error.response.data.errors) {
                    for (
                      let i = 0;
                      i < error.response.data.errors.length;
                      i++
                    ) {
                      if (
                        error.response.data.errors[i].message === 'exists' &&
                        error.response.data.errors[i].path ===
                        'merchant_email'
                      ) {
                        this.validateMerchantNotificationEmailError = true;
                        this.errorMerchantProfileMsg = true;
                      }

                      if (error.response.data.errors[i].message === 'invalid') {
                        this.errorMerchantProfileMsg = true;
                        switch (error.response.data.errors[i].path) {
                          case 'contact_phone':
                            this.validateMerchantContactPhoneError = true;
                            break;
                          case 'contact_email':
                            this.validateMerchantContactEmailError = true;
                            break;
                          case 'website':
                            this.validateMerchantWebsiteError = true;
                            break;
                          case 'address':
                            this.validateMerchantRegisteredAddressError = true;
                            break;
                          case 'id_num':
                            this.validateMerchantIdentificationNumberError = true;
                            break;
                          case 'country':
                            this.validateMerchantCountryError = true;
                            break;
                          case 'technical_email':
                            this.validateMerchantTechnicalError = true;
                            break;
                          case 'finance_emails':
                            this.validateMerchantFinanceError = true;
                            break;
                        }
                      }
                    }
                  }
                }
                else if (error.response.status === 401) {
                  this.unauthorisedAccessForMerchantProfileError = true;
                }
                //if the API call fails due to server error
                else if (error.response.status === 500) {
                  this.apiErrorSavingMerchantProfile = true;
                } else {
                  this.errorMerchantProfileMsg = true;
                }
              });
        }
      }
    },

    checkPermissionHandler(currentActiveMerchant, merchantKey) {
      //check the permission of the user in the default selected merchant
      let currentActiveMerchantId =
        this.merchantsGetter &&
        this.merchantsGetter.find(
          (merchant) => merchant.merchant_key === merchantKey
        ).id;

      //determine to show if the user has permission to change the profile and bank accounts or not
      this.displayProfileAndBankAccount = this.isAllowed(
        this.currentActiveEntityAdminGetter,
        currentActiveMerchantId,
        'banks',
        'W'
      );

      //check if 2FA is enabled for selected Merchant (for profile)
      this.enabled2FAForProfile = this.is2faEnabled(
        currentActiveMerchant,
        'update_profile_details'
      );

      //check if 2FA is enabled for selected Merchant (for bank account)
      this.enabled2FAForBankAccount = this.is2faEnabled(
        currentActiveMerchant,
        'update_bank_account'
      );
    },

    fetchMerchantDetailsHandler(merchantKey) {
      const currentActiveMerchant = this.merchantsGetter.find(
        (item) => item.merchant_key === merchantKey
      );
      const merchantName = currentActiveMerchant.merchant_name;
      const merchantPhone = currentActiveMerchant.phone;
      const merchantEmail = currentActiveMerchant.email;
      const merchantLocalCurrency = currentActiveMerchant.local_currency;

      this.fetchMerchantProfile(merchantKey).then(() => {
        this.selectedMerchantProfile = this.merchantProfileDetailsGetter;
        if (this.selectedMerchantProfile) {
          this.preferredLocalCurrency = this.selectedMerchantProfile.local_currency || merchantLocalCurrency || '';
          this.merchantName = this.selectedMerchantProfile.merchant_name || merchantName || '';
          this.idType = this.selectedMerchantProfile.id_type || '';
          this.uniqueEntityIdentifier = this.selectedMerchantProfile.id_num || '';
          this.companyAddress = this.selectedMerchantProfile.address || '';
          this.companyCountry = this.getCountry(this.selectedMerchantProfile.country) || '';
          this.companyWebsite = this.selectedMerchantProfile.website || '';
          this.contactName = this.selectedMerchantProfile.contact_name || '';
          this.contactPhone = this.selectedMerchantProfile.contact_phone || merchantPhone || '';
          this.contactEmail = this.selectedMerchantProfile.contact_email || merchantEmail || '';
          this.notificationEmail = this.selectedMerchantProfile.merchant_email || '';
          this.technicalEmail = this.selectedMerchantProfile.technical_email || '';
          this.financeEmails = this.selectedMerchantProfile.finance_emails || '';

          if (!this.preferredLocalCurrency) {
            this.hasLocalCurrencyWallet = false;
          }
          this.needsCompanyAddress = !this.companyAddress;
        }
      });

      this.fetchMerchantBankAccounts(merchantKey).then(() => {
        this.selectedBankAccount = this.merchantFirstBankAccount;
        this.setBankAccount(this.selectedBankAccount);
        // use one of the correspondent bank variable to determine showing the correspondent bank UI section when the page is loaded.
        this.hasCorrespondentBank =
          this.correspondentBankAccount.accountName.value !== '';
      });
    },

    bankAccountPayloadHandler(selectedBankAccount, eventPayload) {
      const payload = {};
      // updating merchant's bank account using bank key
      if (selectedBankAccount && selectedBankAccount.bank_key) {
        payload.bank_key = selectedBankAccount.bank_key;
      }
      // creating merchant's bank account
      else {
        payload.merchant_key = this.selectedMerchantKey;
      }

      payload.data = {};
      payload.data.account_num = this.bankAccount.accountNumber.value;
      payload.data.account_name = this.bankAccount.accountName.value;
      payload.data.bank_name = this.bankAccount.bankName.value;
      payload.data.bank_address = this.bankAccount.bankAddress.value;
      payload.data.bank_country = this.bankAccount.bankCountry.value;
      payload.data.swift_bic = this.bankAccount.swiftCode.value;

      if (
        this.bankAccount.bankCountry.value === 'US' &&
        this.bankAccount.routingNumber.value
      ) {
        payload.data.routing_num = this.bankAccount.routingNumber.value;
      }

      if (this.bankAccount.bankClearingCode.value) {
        payload.data.bank_clearing_code = this.bankAccount.bankClearingCode.value || '';
      }

      if (this.hasCorrespondentBank) {
        payload.data.correspondent_bank_account_name = this.correspondentBankAccount.accountName.value || '';
        payload.data.correspondent_bank_account_number = this.correspondentBankAccount.accountNumber.value || '';
        payload.data.correspondent_bank_name = this.correspondentBankAccount.bankName.value || '';
        payload.data.correspondent_bank_address = this.correspondentBankAccount.bankAddress.value || '';
        payload.data.correspondent_bank_country = this.correspondentBankAccount.bankCountry.value !== 'NN' ? this.correspondentBankAccount.bankCountry.value : '';
        payload.data.correspondent_bank_swift_bic = this.correspondentBankAccount.swiftCode.value ? this.correspondentBankAccount.swiftCode.value : '';
        if (this.correspondentBankAccount.bankCountry.value === 'US' && this.correspondentBankAccount.routingNumber.value) {
          payload.data.correspondent_bank_routing_number = this.correspondentBankAccount.routingNumber.value;
        }
        payload.data.correspondent_bank_clearing_code = this.correspondentBankAccount.bankClearingCode.value || '';
      }

      if (eventPayload && Object.keys(eventPayload).length > 0) {
        payload.data[`${Object.keys(eventPayload)[0]}`] = Object.values(
          eventPayload
        )[0];
      }

      return payload;
    },

    saveMerchantBankAccount(eventPayload) {
      this.needsCompanyAddress = false;
      const isBankAccountValid = this.isBankAccountFormValid();
      const isCorrespondentBankValid = this.isCorrespondentBankFormValid();
      if (!this.companyAddress) {
        this.needsCompanyAddress = true;
        return;
      }
      if (
        !isBankAccountValid ||
        !isCorrespondentBankValid ||
        (this.bankAccount.bankCountry.value === 'US' &&
          this.bankAccount.routingNumber.error)
      ) {
        this.errorBankAccountMsg = true;
        return;
      }

      //For new merchant with no bank account
      if (
        this.selectedBankAccount === null ||
        this.selectedBankAccount === undefined ||
        !this.selectedBankAccount
      ) {
        this.saveNewMerchantBankAccount(eventPayload);
      }
      //For merchant with bank account (to edit/update bank account)
      else if (this.selectedBankAccount && this.selectedBankAccount.bank_key) {
        this.saveUpdateMerchantBankAccount(eventPayload);
      }
    },

    saveNewMerchantBankAccount(eventPayload) {
      if (this.selectedMerchantKey) {
        let bankAccountPayload = this.bankAccountPayloadHandler(
          this.selectedBankAccount,
          eventPayload
        );
        if (bankAccountPayload) {
          this.addNewBankAccountDetails(bankAccountPayload)
            .then((res) => {
              if (res.status === 200) {
                this.errorBankAccountMsg = false;
                this.saveBankAccountSuccess = true;
                this.unauthorisedAccessForBankAccountError = false;
                this.selectedBankAccount = res.data;

                setTimeout(() => {
                  this.saveBankAccountSuccess = false;
                }, 3000);
              }
            })
            .catch((err) => {
              if(err.response.status === 401) {
                this.unauthorisedAccessForBankAccountError = true;
              }
              if(err.response.status === 500) {
                //api call error in server
                this.apiSavingMerchantBankDetailsError = true;
              } else {
                console.error('Error in adding new bank account', err);
                this.errorBankAccountMsg = true;
              }
            });
          this.show2faOtpPrompt = false;  
        }
      }
    },

    saveUpdateMerchantBankAccount(eventPayload) {
      let bankAccountPayload = this.bankAccountPayloadHandler(
        this.selectedBankAccount,
        eventPayload
      );
      if (bankAccountPayload) {
        this.updateMerchantBankAccountDetails(bankAccountPayload)
          .then((res) => {
            if (res.status === 200) {
              this.errorBankAccountMsg = false;
              this.unauthorisedAccessForBankAccountError = false;
              this.saveBankAccountSuccess = true;
              this.selectedBankAccount = res.data;
              this.needsCompanyAddress = !this.companyAddress;

              setTimeout(() => {
                this.saveBankAccountSuccess = false;
              }, 3000);
            }
          })
          .catch((err) => {
            console.error('Error in updating new bank account', err.response);
            if (err.response.status === 401) {
              this.unauthorisedAccessForBankAccountError = true;
            } else {
              this.errorBankAccountMsg = true;
            }
          });
        this.show2faOtpPrompt = false;
      }
    },

    // -------------------- VALIDATORS
    isBankAccountFormValid() {
      let isValid = true;
      this.resetAccountErrors(this.bankAccount);

      if (!this.bankAccount.accountName.value) {
        this.bankAccount.accountName.error = true;
        isValid = false;
      }

      if (!this.bankAccount.accountNumber.value) {
        this.bankAccount.accountNumber.error = true;
        isValid = false;
      }

      if (!this.bankAccount.bankName.value) {
        this.bankAccount.bankName.error = true;
        isValid = false;
      }

      if (!this.bankAccount.bankAddress.value) {
        this.bankAccount.bankAddress.error = true;
        isValid = false;
      }

      if (!this.bankAccount.bankCountry.value) {
        this.bankAccount.bankCountry.error = true;
        isValid = false;
      }

      if (
        (!this.bankAccount.swiftCode.value &&
          this.bankAccount.bankCountry.value !== 'US') ||
        (this.bankAccount.bankCountry.value !== 'US' &&
          !this.isSwiftCodeValid(this.bankAccount.swiftCode.value))
      ) {
        this.bankAccount.swiftCode.error = true;
        isValid = false;
      }

      if (this.bankAccount.bankCountry.value === 'US') {
        if (!this.bankAccount.routingNumber.value) {
          this.bankAccount.routingNumber.error = true;
          isValid = false;
        }
      }

      if (this.bankAccount.bankCountry.value === 'AU') {
        if (!this.bankAccount.bankClearingCode.value) {
          this.bankAccount.bankClearingCode.error = true;
          isValid = false;
        }
      }

      return isValid;
    },

    isCorrespondentBankFormValid() {
      if (!this.hasCorrespondentBank) {
        return true;
      }
      let isValid = true;
      this.resetAccountErrors(this.correspondentBankAccount);
      if (!this.correspondentBankAccount.accountName.value) {
        this.correspondentBankAccount.accountName.error = true;
        isValid = false;
      }

      if (!this.correspondentBankAccount.accountNumber.value) {
        this.correspondentBankAccount.accountNumber.error = true;
        isValid = false;
      }

      if (!this.correspondentBankAccount.bankName.value) {
        this.correspondentBankAccount.bankName.error = true;
        isValid = false;
      }

      if (!this.correspondentBankAccount.bankAddress.value) {
        this.correspondentBankAccount.bankAddress.error = true;
        isValid = false;
      }

      if (!this.correspondentBankAccount.bankCountry.value) {
        this.correspondentBankAccount.bankCountry.error = true;
        isValid = false;
      }

      if (
        !this.correspondentBankAccount.swiftCode.value &&
        this.correspondentBankAccount.bankCountry.value !== 'US'
      ) {
        this.correspondentBankAccount.swiftCode.error = true;
        isValid = false;
      }

      if (this.correspondentBankAccount.bankCountry.value === 'US') {
        if (!this.correspondentBankAccount.routingNumber.value) {
          this.correspondentBankAccount.routingNumber.error = true;
          isValid = false;
        }
      }

      return isValid;
    },

    isProfileFormValid() {
      let isValid = true;
      this.resetProfileErrors();

      if (!this.preferredLocalCurrency && this.hasLocalCurrencyWallet) {
        this.validatePreferredLocalCurrencyError = true;
        isValid = false;
      }

      if (!this.idType) {
        this.validateMerchantIdentificationTypeError = true;
        isValid = false;
      }

      if (!this.uniqueEntityIdentifier) {
        this.validateMerchantIdentificationNumberError = true;
        isValid = false;
      }
      if (!this.companyAddress) {
        this.validateMerchantRegisteredAddressError = true;
        isValid = false;
      }

      if (!this.contactName) {
        this.validateMerchantContactNameError = true;
        isValid = false;
      }

      if (!this.contactPhone) {
        this.validateMerchantContactPhoneError = true;
        isValid = false;
      }

      if (!this.companyWebsite) {
        this.validateMerchantWebsiteError = true;
        isValid = false;
      }

      if (
        !this.contactEmail ||
        (this.contactEmail && this.contactEmail.indexOf('@') < 2)
      ) {
        if (this.debugConsole) console.log('Contact Email Error');
        this.validateMerchantContactEmailError = true;
        this.errorMerchantProfileMsg = true;
        isValid = false;
      }

      if (
        !this.notificationEmail ||
        (this.notificationEmail && this.notificationEmail.indexOf('@') < 2)
      ) {
        this.validateMerchantNotificationEmailError = true;
        this.errorMerchantProfileMsg = true;
        isValid = false;
      }

      if (this.technicalEmail && this.technicalEmail.indexOf('@') < 2) {
        this.validateMerchantTechnicalError = true;
        isValid = false;
      }

      if (this.financeEmails) {
        const financeEmailsArr = this.financeEmails.split(',');
        this.validateMerchantFinanceError = false;
        isValid = true;

        for (let email of financeEmailsArr) {
          email = email.trim();
          if (!this.isEmail(email)) {
            this.validateMerchantFinanceError = true;
            isValid = false;
            break;
          }
        }
      }
      this.errorMerchantProfileMsg = !isValid;
      return isValid;
    },

    isSwiftCodeValid(str) {
      return /^[a-zA-Z0-9]{8,11}$/.test(str);
    },

    // ----------- GET SET
    getCountry(code) {
      for (let country of COUNTRIES) {
        if (country.code === code) {
          return country;
        }
      }
    },

    getRecaptcha() {
      return this.$recaptchaLoaded().then(() => {
        return this.$recaptcha('login').then((token) => {
          this.recaptchaToken = token;
        });
      }).catch(() => {
        this.$vs.notify({
          title: 'Recaptcha Error',
          text: 'Error while getting token. Please refresh the page.',
          color: 'danger',
        });
      });
    },

    /**
     * Sets bank account variable to the bank account fetched.
     * @param {*} newVal
     */
    setBankAccount(newVal) {
      if (!newVal) {
        this.resetBankAccount();
        return;
      }
      this.bankAccount.accountName.value = newVal.account_name || '';
      this.bankAccount.accountNumber.value = newVal.account_num || '';
      this.bankAccount.bankName.value = newVal.bank_name || '';
      this.bankAccount.bankAddress.value = newVal.bank_address || '';
      this.bankAccount.bankCountry.value = newVal.bank_country || '';
      this.bankAccount.swiftCode.value = newVal.swift_bic || '';
      this.bankAccount.routingNumber.value = newVal.routing_num || '';
      this.bankAccount.bankClearingCode.value = newVal.bank_clearing_code || '';

      this.correspondentBankAccount.accountName.value = newVal.correspondent_bank_account_name || '';
      this.correspondentBankAccount.accountNumber.value = newVal.correspondent_bank_account_number || '';
      this.correspondentBankAccount.bankAddress.value = newVal.correspondent_bank_address || '';
      this.correspondentBankAccount.bankCountry.value = newVal.correspondent_bank_country || '';
      this.correspondentBankAccount.bankName.value = newVal.correspondent_bank_name || '';
      this.correspondentBankAccount.swiftCode.value = newVal.correspondent_bank_swift_bic || '';
      this.correspondentBankAccount.routingNumber.value = newVal.correspondent_bank_routing_number || '';
      this.correspondentBankAccount.bankClearingCode.value = newVal.correspondent_bank_clearing_code || '';
      this.hasCorrespondentBank = this.correspondentBankAccount.accountName.value !== '';
    },

    resetBankAccount() {
      this.bankAccount = {
        accountName: {
          value: '',
          error: false,
        },
        accountNumber: {
          value: '',
          error: false,
        },
        bankName: {
          value: '',
          error: false,
        },
        bankCountry: {
          value: '',
          error: false,
        },
        swiftCode: {
          value: '',
          error: false,
        },
        routingNumber: {
          value: '',
          error: false,
        },
        bankAddress: {
          value: '',
          error: false,
        },
        bankClearingCode: {
          value: '',
          error: false,
        },
      };

      this.correspondentBankAccount = {
        accountName: {
          value: '',
          error: false,
        },
        accountNumber: {
          value: '',
          error: false,
        },
        bankName: {
          value: '',
          error: false,
        },
        bankCountry: {
          value: '',
          error: false,
        },
        swiftCode: {
          value: '',
          error: false,
        },
        routingNumber: {
          value: '',
          error: false,
        },
        bankAddress: {
          value: '',
          error: false,
        },
        bankClearingCode: {
          value: '',
          error: false,
        },
      };

      this.hasCorrespondentBank = false;
    },

    resetAccountErrors(acc) {
      for (let param of Object.keys(acc)) {
        acc[param].error = false;
      }
    },

    resetProfileErrors() {
      this.validatePreferredLocalCurrencyError = false;
      this.validateMerchantIdentificationTypeError = false;
      this.validateMerchantIdentificationNumberError = false;
      this.validateMerchantRegisteredAddressError = false;
      this.validateMerchantCountryError = false;
      this.validateMerchantContactNameError = false;
      this.validateMerchantContactPhoneError = false;
      this.validateMerchantNotificationEmailError = false;
      this.validateMerchantContactEmailError = false;
      this.validateMerchantWebsiteError = false;
      this.validateMerchantTechnicalError = false;
      this.validateMerchantFinanceError = false;
    },
  },
};
</script>

<style lang="scss">
.profile-page-wrapper {
  h1 {
    font-weight: 700;
    color: #505050;
    margin-top: 24px;
  }

  form {
    fieldset {
      border: none;
      display: grid;
      grid-template-columns: repeat(auto-fill, 390px);
      grid-gap: 20px;
      grid-template-rows: auto;
      width: 100%;
      max-width: 800px;
      margin-top: 24px;

      legend {
        font-size: 24px;
        font-weight: 700;
        margin-bottom: 12px;
      }

      p.banner.info {
        color: #856404;
        background-color: #fff3cd;
        border: 1px solid #ffeeba;
        border-radius: 8px;
        padding: 10px;
        grid-column-start: 1;
        grid-column-end: -1;

        &>a {
          font-weight: 700;
          text-decoration: underline;
          color: #856404;

          &:hover {
            text-decoration: underline;

          }
        }
      }
    }

    label,
    &>div {
      display: flex;
      flex-wrap: wrap;
      min-width: 300px;

      input,
      select,
      textarea {
        width: 100%;
        display: block;
        min-height: 40px;
        border: 1px solid #ccc;
        border-radius: 5px;
        padding-left: 12px;
      }

      p {
        width: 100%;
        font-style: italic;
      }

      &.full-row {
        grid-column-start: 1;
        grid-column-end: -1;
      }

      &.withdrawal-account {
        max-width: 800px;
      }
    }

    div.errors-msg {
      color: red;
      font-size: 12px;
      grid-column-start: 1;
      grid-column-end: -1;
    }

    div.success-msg {
      color: green;
      font-size: 12px;
      grid-column-start: 1;
      grid-column-end: -1;
      display:flex;
      align-items: center;
    }

    button {
      // save button
      max-width: 200px;
      background-color: $color-success-darker;
    }
  }

  .action-group {
    width: 100%;
    margin-top: 12px;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    max-width: 800px;

    button {
      min-width: 250px;
      background-color: $color-success-darker;
    }

    &>input[type='checkbox'] {
      display: none;
      position: relative;

      &+label {
        // no correspondent bank style
        padding: 12px;
        display: inline-block;
        background-color: $color-primary;
        color: white;
        border: none;
        border-radius: 5px;
        transition: 0.2s ease-in-out;
        position: relative;
        height: 48px;
        text-align: center;
        width: fit-content;

        &:hover {
          cursor: pointer;
          transform: translateY(1px);
          box-shadow: inset 0px 10px 20px 2px rgba(0, 0, 0, 0.25);
        }
      }

      &:checked+label {
        // has correspondent bank style
        background-color: white;
        color: red;
        border: 1px solid red;
        border-radius: 5px;
        transition: 0.2s ease-in-out;

        &:hover {
          background-color: red;
          color: white;
        }
      }
    }
  }

  button {
    height: 48px;
    min-width: 120px;
    padding: 12px;
    background-color: $color-primary;
    color: white;
    border: none;
    border-radius: 5px;
    transition: 0.2s ease-in-out;

    &:hover {
      cursor: pointer;
      transform: translateY(1px);
      box-shadow: inset 0px 10px 20px 2px rgba(0, 0, 0, 0.25);
    }

    &:disabled {
      background-color: #cccccc;
      color: #666666;
      cursor: not-allowed;
      &:hover {
        transform: none;
        box-shadow: none;
        cursor: not-allowed;
      }
    }
  }

  .account-selection,
  .tel-selection {
    border-radius: 5px;
    border: 1px solid #ccc;
    padding: 0.5rem;
    background-color: white !important;
    font-family: 'Montserrat', Helvetica, Arial, sans-serif;
    font-size: 1rem;
    width: 100%;
    height: 40px;

    input[type='tel'] {
      min-height: auto;
    }
    &:disabled, &.disabled {
      background-color: rgba(239, 239, 239, 0.3) !important;
      cursor: not-allowed;
      &:hover {
        transform: none;
        box-shadow: none;
        cursor: not-allowed;
      }
    }
  }

  .input-text-style {
    padding: 0.7rem;
    font-size: 1rem;
    border-radius: 5px;
    border: 1px solid #cccccc;
    font-family: 'Montserrat', Helvetica, Arial, sans-serif !important;
  }

  .input-textarea-style {
    height: 65px;
    font-size: 1rem;
    border-radius: 5px;
    padding: 0.7rem;
    border: 1px solid rgb(204, 204, 204);
    font-family: Montserrat, Helvetica, Arial, sans-serif !important;
  }

  .vs-input--input.normal {
    padding: 0.7rem;
    font-size: 1rem;
    font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  }

  input[type='tel'] {
    font-family: 'Montserrat', Helvetica, Arial, sans-serif !important;
  }

  .vti__input {
    border: none;
    border-radius: 0 2px 2px 0;
    width: 100%;
    outline: 0;
    padding-left: 7px;
  }

  .input-error-style {
    border: 1px solid #ff0000;
  }

  .tooltip {
    position: relative;
    display: inline-block;

    svg {
      margin-left: 6px;
      width: 12px;
      height: 12px;
    }

    &>span {
      visibility: hidden;
      display: flex;
      flex-direction: column;
      background-color: lightslategray;
      width: 275px;
      padding: 10px;
      border-radius: 5px;
      color: #ffffff;
      font-size: 10px;
      text-align: center;
      position: absolute;
      z-index: 1;
      left: 105%;
      top: -20px;
    }

    &:hover {
      &>span {
        visibility: visible;
      }
    }
  }

  .save-bank-success {
    color: green;
    display: flex;
    align-items: center;
  }

  .save-error{
    color: red;
  }
}
</style>
